import { guardNever } from "@hex/common";
import { debounce, noop, throttle } from "lodash";
import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import { Flex, FlexProps } from "../../../../hex-components/Flex";
import { composeRefs } from "../../../../hooks/useCompositeRef";
import { useStableRef } from "../../../../hooks/useStableRef";
import {
  Size,
  canReliablyMeasureElements,
  getElementTransform,
} from "../../../../util/geometry";

export interface DynamicFlexProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Flex>,
  "children"
> {
  renderEndDecorator: (
    props: { ref: (el: HTMLElement | null) => void },
    state: { count: number; overflowCount: number },
  ) => React.ReactNode;
  strategy?: "complete" | "partial";
  partialMinWidth?: number;
  count: number;
  children: (state: { overflowCount: number }) => React.ReactNode;
  onOverflowingChange?: (overflowing: boolean) => void;
}

/**
 * A flex container that's aware of how many children it can fit in itself.
 * Provides a slot for an end decorator that is rendered when the children
 * overflow.
 *
 * Not ready for wider use yet, pretty hacky.
 * Adapted from Hashboard's DynamicFlex component.
 */
export const DynamicFlex = React.memo(
  React.forwardRef<HTMLDivElement, DynamicFlexProps>(function DynamicFlex(
    {
      children: renderChildren,
      count,
      direction = "row",
      onOverflowingChange,
      partialMinWidth,
      renderEndDecorator,
      strategy = "complete",
      ...rest
    },
    ref,
  ) {
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    const [sizerEl, setSizerEl] = useState<HTMLElement | null>(null);
    const [endDecoratorEl, setEndDecoratorEl] = useState<HTMLElement | null>(
      null,
    );

    const [visibleCount, setVisibleCount] = useState(0);
    const overflowCount = count - visibleCount;

    const children = useMemo(() => {
      return renderChildren({ overflowCount: overflowCount });
    }, [renderChildren, overflowCount]);

    const childrenArray = React.Children.toArray(children);

    const recompute = useStableRef(() => {
      if (!containerEl || !sizerEl || !canReliablyMeasureElements()) return;
      const recomputedVisibleCount = calculateVisibleItemCount({
        containerEl,
        endDecoratorEl,
        count,
        direction,
        partialMinWidth,
        sizerEl,
        strategy,
      });
      if (recomputedVisibleCount !== visibleCount) {
        setVisibleCount(recomputedVisibleCount);
      }
      onOverflowingChange?.(recomputedVisibleCount !== count);
    });

    useLayoutEffect(() => {
      recompute.current();
    });

    useOnElementResize(() => recompute.current(), containerEl, 100, throttle);

    const childrenBeforeOverflow = childrenArray.slice(0, visibleCount);

    const endDecorator = useMemo(
      () =>
        renderEndDecorator(
          { ref: setEndDecoratorEl },
          { count, overflowCount },
        ),
      [renderEndDecorator, count, overflowCount],
    );

    const containerRef = composeRefs(ref, setContainerEl);

    return (
      <DynamicFlexContainer
        ref={containerRef}
        direction={direction}
        justify="start"
        {...rest}
        data-dynamic-flex-render="visible"
      >
        {childrenBeforeOverflow}
        {endDecorator}

        {/* invisible render used to count how much content can fit */}
        <DynamicFlexSizer ref={setSizerEl} justify="start" {...rest}>
          <DynamicFlexSizerCtx.Provider value={true}>
            {children}
            {endDecorator}
          </DynamicFlexSizerCtx.Provider>
        </DynamicFlexSizer>
      </DynamicFlexContainer>
    );
  }),
);

const DynamicFlexContainer = styled(Flex)`
  position: relative;
  width: 100%;
  height: 100%;
`;

const DynamicFlexSizer = styled(Flex)`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
`;

const DynamicFlexSizerCtx = createContext(false);
export const useIsInsideDynamicFlexSizer = () =>
  useContext(DynamicFlexSizerCtx);

function calculateVisibleItemCount(params: {
  containerEl: HTMLElement;
  sizerEl: HTMLElement;
  endDecoratorEl: HTMLElement | null;
  direction: FlexProps["direction"];
  strategy: "complete" | "partial";
  count: number;
  partialMinWidth: number | undefined;
}): number {
  const {
    containerEl,
    count,
    direction,
    endDecoratorEl,
    partialMinWidth,
    sizerEl,
    strategy,
  } = params;

  const horizontal = direction === "row" || direction === "row-reverse";
  const reverse = direction === "row-reverse" || direction === "column-reverse";

  const property = horizontal ? "clientWidth" : "clientHeight";

  const endDecoratorSize = endDecoratorEl?.[property] ?? 0;
  const availableSize = containerEl[property] - endDecoratorSize;
  const maxSize = sizerEl[property] - endDecoratorSize;

  const orderedChildren = Array.from(sizerEl.children);

  const [startIdx, endIdx] = reverse
    ? [0, orderedChildren.length - 1]
    : [orderedChildren.length - 1, 0];
  let currentSize = maxSize;
  for (
    let nodeIdx = startIdx;
    nodeIdx >= endIdx;
    reverse ? (nodeIdx += 1) : (nodeIdx -= 1)
  ) {
    if (currentSize <= availableSize) {
      if (strategy === "complete") {
        return Math.min(nodeIdx + 1, count);
      } else if (strategy === "partial") {
        if (partialMinWidth != null) {
          if (availableSize - currentSize >= partialMinWidth) {
            return Math.min(nodeIdx + 2, count);
          } else {
            return Math.min(nodeIdx + 1, count);
          }
        } else {
          return Math.min(nodeIdx + 2, count);
        }
      } else {
        guardNever(strategy, strategy);
      }
    }

    const node = orderedChildren[nodeIdx];
    const nodeSize = node[property];
    currentSize -= nodeSize;
  }
  if (strategy === "partial") {
    if (partialMinWidth != null) {
      if (availableSize >= partialMinWidth) {
        return Math.min(1, count);
      } else {
        return 0;
      }
    } else {
      return Math.min(1, count);
    }
  }
  return 0;
}

/**
 * Runs the provided callback whenever a resize event occurs on the element.
 * Updates are debounced.

 * Note: this is similar to useResizeObserver, but that function only accepts
 * element IDs, not does not debounce events, and has been observed to cause
 * "ResizeObserver loop limit exceeded" errors. Also it uses resize entries
 * to determine dimensions whereas this function just runs `getElementTransform`.
 */
const useOnElementResize = (
  callback: (size: Size) => void,
  // NOTE: we support null here so that document.querySelector() can be used to locate the element
  element: HTMLElement | null,
  debounceMs = 250,
  limiter: typeof debounce | typeof throttle = debounce,
) => {
  // We capture the callback in a ref so we can use it in hooks without requiring the callback's identity to be stable
  const callbackRef = useStableRef(callback);

  // Initialize (using layout effect so that first render completes)
  useLayoutEffect(() => {
    if (element) {
      callbackRef.current(getElementTransform(element));
    }
  }, [element, callbackRef]);

  const onResize = useMemo(
    () =>
      limiter(() => {
        if (element) {
          callbackRef.current(getElementTransform(element));
        }
      }, debounceMs),
    [element, debounceMs, limiter, callbackRef],
  );

  // Create resize observer
  useEffect(() => {
    if (!element) return noop;

    const observer = new ResizeObserver(onResize);
    observer.observe(element);

    // cleanup
    return () => {
      observer.disconnect();
    };
  }, [element, onResize]);
};
